import React from 'react';
import { EEthereumNetworkId } from '@ankr.com/provider';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import { ZERO } from 'modules/common';
import { useTranslation } from 'modules/i18n';
import { useGetNativeTokenUSDPriceQuery } from 'modules/store';

import {
  GetTaskExecutionsResult,
  useGetExecutionsQuery,
} from '../../actions/getExecutions';
import { useExecutionsContent } from '../../hooks/useExecutionsContent';
import { ExpandedExecutionLog } from '../ExpandedExecutionLog';
import translation from './executions.translation';

interface Props {
  taskId: string;
  chainId: EEthereumNetworkId;
}

const EMPTY_EXECUTIONS: GetTaskExecutionsResult = [];

export function Executions({ taskId, chainId }: Props): JSX.Element {
  const { t, keys } = useTranslation(translation);
  const { classes, cx } = useStyles();

  const { data: usdPrice = ZERO } = useGetNativeTokenUSDPriceQuery({ chainId });
  const { data = EMPTY_EXECUTIONS } = useGetExecutionsQuery({
    taskId,
    chainId,
    usdPrice,
  });

  const executions = useExecutionsContent(data, chainId);

  return (
    <div>
      <TableContainer className={classes.tableContainer}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell className={cx(classes.cell, classes.headCell)}>
                {t(keys.dateCell)}
              </TableCell>

              <TableCell className={cx(classes.cell, classes.headCell)}>
                {t(keys.txHashCell)}
              </TableCell>

              <TableCell className={cx(classes.cell, classes.headCell)}>
                {t(keys.statusCell)}
              </TableCell>

              <TableCell
                align="right"
                className={cx(classes.cell, classes.headCell)}
              >
                {t(keys.costCell)}
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {executions.map(({ id, executedAt, txHash, cost, status }) => (
              <TableRow key={id}>
                <TableCell className={classes.cell}>{executedAt}</TableCell>

                <TableCell className={classes.cell}>{txHash}</TableCell>

                <TableCell className={classes.cell}>{status}</TableCell>

                <TableCell className={classes.cell} align="right">
                  {cost}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <div className={classes.mobileContainer}>
        <Typography
          variant="subtitle3"
          color={theme => theme.palette.grey[600]}
          mb={3}
        >
          {t(keys.dateCell)}
        </Typography>

        {executions.map(
          ({ id, executedAt, txHash, status, statusDot, cost }) => (
            <ExpandedExecutionLog
              key={id}
              className={classes.expandedExecutionLog}
              executedAt={executedAt}
              txHash={txHash}
              status={status}
              statusDot={statusDot}
              cost={cost}
            />
          ),
        )}
      </div>
    </div>
  );
}

const useStyles = makeStyles()(theme => ({
  tableContainer: {
    '&&': {
      [theme.breakpoints.down('md')]: {
        display: 'none',
      },
    },
  },
  headCell: {
    '&&': {
      backgroundColor: 'transparent',
    },
  },
  cell: {
    '&&': {
      '&:first-of-type': {
        paddingLeft: 0,
      },
      '&:last-of-type': {
        paddingRight: 0,
      },
    },
  },
  mobileContainer: {
    display: 'flex',
    flexDirection: 'column',

    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },

  expandedExecutionLog: {
    '&:not(:last-of-type)': {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },

  log: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
}));
